import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  ShoppingCart,
  Bell,
  Sun,
  Moon,
  Globe,
  Menu,
  X,
  MapPin,
  ChevronDown,
  User,
  LogOut,
} from "lucide-react";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useCart } from "@/contexts/CartContext";
import { useBrand } from "@/contexts/BrandContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useBranchModal } from "@/contexts/BranchModalContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { useFirebaseNotifications } from "@/hooks/useFirebaseNotifications";
import { Button } from "@/components/ui/button";
import { useAuthGate } from "@/hooks/useAuthGate";
import { FaWhatsapp } from "react-icons/fa";
export default function Header() {
  const { language, setLanguage, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { itemsCount: totalItems } = useCart();
  const { brand, localizedName } = useBrand();
  const { session } = useSessionContext();
  const { openBranchModal } = useBranchModal();
  const { requireAuth } = useAuthGate();
  const navigate = useNavigate();
  const { isAuthenticated, isGuest, customerName, openLogin, handleLogout } =
    useCustomerAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const branchName = session.order.branchName;
  const orderType = session.order.order_type;

  const orderTypeLabel =
    orderType === "delivery"
      ? language === "ar"
        ? "توصيل"
        : "Delivery"
      : orderType === "dine_in"
        ? language === "ar"
          ? "داخل المطعم"
          : "Dine In"
        : language === "ar"
          ? "استلام"
          : "Pickup";

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { to: "/", label: t("nav.home"), requiresAuth: false },
    { to: "/menu", label: t("nav.menu"), requiresAuth: false },
    { to: "/cart", label: t("nav.cart"), requiresAuth: true },
    { to: "/track", label: t("nav.track"), requiresAuth: true },
    { to: "/profile", label: t("nav.profile"), requiresAuth: true },
  ];
  // Derive mobile from session — stored as customer.data.mobile or customer.data.phone_number
  const mobile: string | null =
    (session.customer.data?.mobile as string) ||
    (session.customer.data?.phone_number as string) ||
    null;

  const restaurantId: string | null = session.order.restaurantId || null;

  const {
    notifications: fbNotifications,
    markRead,
    markAllRead,
  } = useFirebaseNotifications(mobile, restaurantId);

  const unreadCount = fbNotifications.filter((n) => !n.read).length;

  const normalizedWhatsAppNumber = brand?.whatsAppNumber?.trim()
    ? brand.whatsAppNumber.replace(/[^\d]/g, "")
    : "";

  const handleWhatsAppClick = () => {
    if (!normalizedWhatsAppNumber) return;

    const message = language === "ar" ? "عرض القائمة" : "show menu";

    window.open(
      `https://wa.me/${normalizedWhatsAppNumber}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <nav className="container mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          {brand.logoType === "image" ? (
            <img
              src={brand.logo}
              alt={localizedName(language)}
              className="h-8 w-8 object-contain"
            />
          ) : (
            <span className="text-2xl">{brand.logo}</span>
          )}
          <span className="font-heading text-xl font-extrabold text-primary">
            {localizedName(language)}
          </span>
        </Link>

        {/* Branch Chip */}
        {branchName && (
          <button
            onClick={() => openBranchModal()}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm font-medium text-foreground hover:bg-muted/80 transition-colors"
          >
            <MapPin className="h-3.5 w-3.5 text-primary" />
            <span className="max-w-[120px] truncate">{branchName}</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground">
              {orderTypeLabel}
            </span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>
        )}

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <li key={link.to}>
              {link.requiresAuth ? (
                <button
                  onClick={() => requireAuth(() => navigate(link.to))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  to={link.to}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* WhatsApp */}
          {normalizedWhatsAppNumber && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleWhatsAppClick}
              aria-label="WhatsApp"
              className="hover:bg-green-500/10"
            >
              <FaWhatsapp className="h-5 w-5 text-green-500" />
            </Button>
          )}

          {/* Cart */}
          <button
            className="relative"
            onClick={() => requireAuth(() => navigate("/cart"))}
            aria-label="Cart"
          >
            <ShoppingCart className="h-5 w-5" />
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -end-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                {totalItems}
              </span>
            )}
          </button>

          {/* Notifications — only shown when user has mobile */}
          {mobile && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setNotifOpen(!notifOpen);
                  if (!notifOpen) markAllRead();
                }}
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -end-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {notifOpen && (
                <div className="absolute end-0 top-full mt-2 w-80 rounded-lg border border-border bg-popover p-2 shadow-lg z-50 max-h-96 overflow-y-auto">
                  {fbNotifications.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {language === "ar"
                        ? "لا توجد إشعارات"
                        : "No notifications"}
                    </p>
                  ) : (
                    fbNotifications.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => {
                          markRead(n.id);
                          setNotifOpen(false);
                          navigate(`/track/${n.orderId}`);
                        }}
                        className={`w-full text-start p-3 rounded-md text-sm mb-1 transition-colors hover:bg-muted/80 ${
                          n.read
                            ? "text-muted-foreground"
                            : "bg-muted font-medium text-foreground"
                        }`}
                      >
                        <p>{language === "ar" ? n.messageAr : n.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {language === "ar" ? n.timeAr : n.time}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Login / User */}
          {isAuthenticated ? (
            <div className="hidden md:flex items-center gap-1">
              <span className="text-sm font-medium text-foreground max-w-[80px] truncate">
                {customerName}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="hidden md:flex gap-1.5"
              onClick={() => openLogin()}
            >
              <User className="h-4 w-4" />
              <span>{language === "ar" ? "دخول" : "Login"}</span>
            </Button>
          )}

          {/* Language */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLanguage(language === "en" ? "ar" : "en")}
            aria-label="Toggle language"
          >
            <Globe className="h-5 w-5" />
          </Button>

          {/* Theme */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {isDark ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </nav>

      {/* Mobile branch chip */}
      {branchName && (
        <div className="md:hidden border-t border-border bg-background px-4 py-2">
          <button
            onClick={() => openBranchModal()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm font-medium text-foreground w-full justify-center"
          >
            <MapPin className="h-3.5 w-3.5 text-primary" />
            <span className="max-w-[160px] truncate">{branchName}</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground">
              {orderTypeLabel}
            </span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-background px-4 pb-4">
          {navLinks.map((link) =>
            link.requiresAuth ? (
              <button
                key={link.to}
                onClick={() => {
                  setMobileOpen(false);
                  requireAuth(() => navigate(link.to));
                }}
                className={`block w-full text-start py-3 text-sm font-medium border-b border-border ${
                  isActive(link.to) ? "text-primary" : "text-foreground"
                }`}
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block py-3 text-sm font-medium border-b border-border ${
                  isActive(link.to) ? "text-primary" : "text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ),
          )}
          {/* Mobile login/logout */}
          {isAuthenticated ? (
            <button
              onClick={() => {
                handleLogout();
                setMobileOpen(false);
              }}
              className="flex items-center gap-2 w-full py-3 text-sm font-medium text-destructive"
            >
              <LogOut className="h-4 w-4" />
              {language === "ar" ? "تسجيل الخروج" : "Logout"}
            </button>
          ) : (
            <button
              onClick={() => {
                openLogin();
                setMobileOpen(false);
              }}
              className="flex items-center gap-2 w-full py-3 text-sm font-medium text-primary"
            >
              <User className="h-4 w-4" />
              {language === "ar" ? "تسجيل الدخول" : "Login"}
            </button>
          )}
        </div>
      )}
    </header>
  );
}
